"use client";

import type { RefObject } from "react";

import { useCallback, useEffect, useRef, useState } from "react";

import { useNativeAudio } from "@/app/_providers/native-audio";
import { getOrCreateMediaElementSource } from "@/app/_ui/components/helpers/mediaElementSource";
import { logger } from "@utilities/logger";

type AudioElementOrbAnalyserOptions = {
  outputMuted?: boolean;
};

function syncOutputGain(outputGain: GainNode, audioElement: HTMLMediaElement, outputMuted: boolean) {
  const isSilent = outputMuted || audioElement.muted || audioElement.volume === 0;

  outputGain.gain.setValueAtTime(isSilent ? 0 : 1, outputGain.context.currentTime);
}

export function useNativeAudioOrbAnalyser(sourceName: string) {
  const { audioRef } = useNativeAudio();

  return useAudioElementOrbAnalyser(audioRef, sourceName);
}

export function useAudioElementOrbAnalyser(
  audioRef: RefObject<HTMLMediaElement | null>,
  sourceName: string,
  options: AudioElementOrbAnalyserOptions = {},
) {
  const outputMuted = options.outputMuted ?? false;
  const [outputAnalyser, setOutputAnalyser] = useState<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const outputGainRef = useRef<GainNode | null>(null);
  const outputMutedRef = useRef(outputMuted);

  useEffect(() => {
    outputMutedRef.current = outputMuted;

    const audioElement = audioRef.current;
    const outputGain = outputGainRef.current;
    if (!audioElement || !outputGain) return;

    syncOutputGain(outputGain, audioElement, outputMuted);
  }, [audioRef, outputMuted]);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) {
      setOutputAnalyser(null);
      audioContextRef.current = null;
      outputGainRef.current = null;
      return;
    }

    try {
      const { ctx, src } = getOrCreateMediaElementSource(audioElement);
      const analyser = ctx.createAnalyser();
      const outputGain = ctx.createGain();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.8;

      syncOutputGain(outputGain, audioElement, outputMutedRef.current);

      // Keep the analyser before the gain so muted previews still animate without reaching the speakers.
      src.connect(analyser);
      analyser.connect(outputGain);
      outputGain.connect(ctx.destination);

      audioContextRef.current = ctx;
      outputGainRef.current = outputGain;
      setOutputAnalyser(analyser);

      const handleVolumeChange = () => {
        syncOutputGain(outputGain, audioElement, outputMutedRef.current);
      };

      audioElement.addEventListener("volumechange", handleVolumeChange);

      return () => {
        audioContextRef.current = null;
        if (outputGainRef.current === outputGain) {
          outputGainRef.current = null;
        }
        setOutputAnalyser(current => (current === analyser ? null : current));
        audioElement.removeEventListener("volumechange", handleVolumeChange);
        src.disconnect(analyser);
        analyser.disconnect(outputGain);
        outputGain.disconnect();
      };
    } catch (error) {
      audioContextRef.current = null;
      outputGainRef.current = null;
      setOutputAnalyser(null);
      logger.warn(`[${sourceName}] Failed to initialize orb audio analyser`, { error });
    }
  }, [audioRef, sourceName]);

  const ensureAudioContext = useCallback(() => {
    const audioContext = audioContextRef.current;
    if (!audioContext || audioContext.state !== "suspended") return;

    void audioContext.resume().catch(error => {
      logger.warn(`[${sourceName}] Failed to resume orb audio context`, { error });
    });
  }, [sourceName]);

  return { ensureAudioContext, outputAnalyser };
}
